import React, { useCallback, useRef, useState } from 'react'
import { useForm } from '@mantine/form'
import { Box, TextInput } from '@mantine/core'
import { Dialog } from 'antd-mobile'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { TemplateMerchantInterface } from '../../typings/orderTypes'
import { IconBriefcase, IconId, IconPhone, IconUser } from '@tabler/icons-react'
import { usePaymentChannels } from '../../hooks/usePaymentChannels'
import { getThemeConfig } from '../../utils/themeConfig'
import TrustBadgeCard from './TrustBadgeCard'
import FormHeader from './FormHeader'
import StepIndicator from './StepIndicator'
import SubmitButton from './SubmitButton'
import { useThreeFactorVerification } from '../hooks/useThreeFactorVerification'
import { useOrderCreation } from '../hooks/useOrderCreation'
import { useTermsOfService } from '../hooks/useTermsOfService'
import { useCompanySearch } from '../hooks/useCompanySearch'
import VerificationCodeInput from './shared/VerificationCodeInput'
import TermsCheckbox from './shared/TermsCheckbox'
import CompanySearchInput from './company/CompanySearchInput'
import { showNotification } from '../../utils/notificationUtil'
import { detectFaceidAuth } from '../../api/orderService'
import getCaptcha from 'dynamic-tencent-captcha'
import { normalizeName, validateRequiredName } from '../../utils/nameValidation'

interface QueryCompanyReportFormProps {
  template: TemplateMerchantInterface | undefined
}

export default function QueryCompanyReportForm({ template }: QueryCompanyReportFormProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const linkId = searchParams.get('linkId') || ''
  const employeeId = searchParams.get('employeeId') || ''
  const showEmployeeCode = searchParams.get('showEmployeeCode') === 'true'
  const [shouldUseFaceId, setShouldUseFaceId] = useState(false)
  const [verificationMode, setVerificationMode] = useState<'sms' | 'faceid'>('sms')

  const { getRoutersString } = usePaymentChannels({ linkId: linkId })
  const themeConfig = getThemeConfig(template?.themeType as number)

  const { searchResults, searching, searchCompany, clearResults } = useCompanySearch()

  const form = useForm({
    initialValues: {
      companyName: '',
      name: '',
      phone: '',
      idCard: '',
      termsOfService: false,
      companyCreditCode: '',
      smsCode: '',
      employeeCode: '',
    },
    validate: {
      companyName: (value) => (value === '' ? '请输入公司名称' : null),
      phone: (value) => (value === '' || !/^1[3-9]\d{9}$/.test(value) ? '请输入正确的法人或股东手机号' : null),
      smsCode: (value) =>
        shouldUseFaceId || verificationMode === 'faceid'
          ? null
          : value === ''
            ? '请输入验证码'
            : value.length !== 6
              ? '请输入6位的验证码'
              : null,
      idCard: (value) =>
        value === '' || !/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)
          ? '请输入正确的法人或股东身份证号'
          : null,
      name: (value) => validateRequiredName(value, '请输入法人或股东姓名'),
      employeeCode: (value) => {
        if (!showEmployeeCode || value === '') return null
        if (!/^\d{1,5}$/.test(value)) return '员工编号必须是1-5位数字'
        return null
      },
    },
  })

  const { getTermsContent } = useTermsOfService()

  const { createOrder, loading: creatingOrder } = useOrderCreation({
    linkId,
    employeeId,
    templateName: template?.name,
    templatePrice: template?.price,
    getRoutersString,
  })

  const captchaRef = useRef<{ ticket: string; randstr: string } | null>(null)
  const orderValuesRef = useRef<typeof form.values | null>(null)

  const { verify: verifyThreeFactor } = useThreeFactorVerification({
    linkId,
    employeeId,
    onSuccess: async () => {
      if (captchaRef.current && orderValuesRef.current) {
        await createOrder({
          ...orderValuesRef.current,
          ticket: captchaRef.current.ticket,
          randstr: captchaRef.current.randstr,
        })
      }
    },
    onError: () => {},
  })

  const handleCompanySelect = (company: any) => {
    form.setFieldValue('companyName', company.name)
    form.setFieldValue('companyCreditCode', company.creditCode)
    clearResults()
  }

  const handleSwitchToFaceid = useCallback(() => {
    Dialog.confirm({
      title: '切换为人脸识别验证',
      content:
        '您将使用人脸识别进行验证，请确保：\n\n1. 输入的姓名和身份证号准确无误\n2. 光线充足且正对摄像头\n\n提交后将跳转到人脸识别页面',
      confirmText: '确认切换',
      cancelText: '不切换',
      onConfirm: () => {
        setVerificationMode('faceid')
        form.setFieldValue('smsCode', '')
      },
    })
  }, [form])

  const executeQuery = async (values: typeof form.values) => {
    try {
      const normalizedValues = { ...values, name: normalizeName(values.name) }
      orderValuesRef.current = normalizedValues
      form.setFieldValue('name', normalizedValues.name)
      const captchaRes = await getCaptcha('195603958')
      captchaRef.current = captchaRes

      if (shouldUseFaceId || verificationMode === 'faceid') {
        Dialog.confirm({
          content: '为了保障您的信息安全，接下来需要进行人脸识别验证，请确保光线充足并正对摄像头。',
          confirmText: '开始验证',
          cancelText: '取消',
          onConfirm: async () => {
            try {
              const res = await detectFaceidAuth({
                ...normalizedValues,
                smsCode: normalizedValues.smsCode || '',
                linkId: linkId,
                employeeId: employeeId,
                employeeCode: normalizedValues.employeeCode || '',
                ticket: captchaRes.ticket,
                randstr: captchaRes.randstr,
              })
              if (res?.url) {
                window.location.href = res.url
              } else {
                showNotification.error('人脸识别验证初始化失败，请稍后再试')
              }
            } catch (error) {
              showNotification.error('人脸识别验证失败，请检查网络后重试')
            }
          },
        })
      } else {
        verifyThreeFactor({
          ...normalizedValues,
          ticket: captchaRes.ticket,
          randstr: captchaRes.randstr,
        })
      }
    } catch {
      showNotification.error('请完成验证后再次尝试')
    }
  }

  const handleSubmit = async (values: typeof form.values) => {
    if (values.companyCreditCode === '') {
      Dialog.alert({
        content: '请通过搜索选择要查询的公司',
        confirmText: '确定',
      })
      return
    }

    if (!values.termsOfService) {
      Dialog.confirm({
        content: getTermsContent(),
        confirmText: '同意并查询',
        cancelText: '不同意',
        onConfirm: () => {
          form.setFieldValue('termsOfService', true)
          executeQuery(values)
        },
      })
    } else {
      executeQuery(values)
    }
  }

  return (
    <Box px={{ base: 'md', lg: 'lg' }}>
      <div className="hidden lg:block">
        <TrustBadgeCard themeConfig={themeConfig} />
      </div>
      <StepIndicator themeConfig={themeConfig} />
      <FormHeader
        templateName={template?.name}
        templateId={template?.templateId}
        onSampleClick={() => navigate('/reportSamples/' + template?.templateId)}
      />
      <div className={'text-gray-500 text-sm mb-4'}>为保证数据准确，请输入真实信息</div>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <CompanySearchInput
          value={form.values.companyName}
          onChange={(value) => form.setFieldValue('companyName', value)}
          error={form.errors.companyName}
          searchResults={searchResults}
          searching={searching}
          onSearch={() => searchCompany(form.values.companyName)}
          onSelect={handleCompanySelect}
        />

        <TextInput
          mt="md"
          placeholder="请输入法人或股东姓名"
          leftSection={<IconUser size={16} />}
          {...form.getInputProps('name')}
        />

        <TextInput
          mt="md"
          placeholder="请输入法人或股东身份证号"
          leftSection={<IconId size={16} />}
          {...form.getInputProps('idCard')}
        />

        <TextInput
          mt="md"
          placeholder="请输入法人或股东手机号"
          leftSection={<IconPhone size={16} />}
          {...form.getInputProps('phone')}
        />

        {!shouldUseFaceId && verificationMode === 'sms' && (
          <VerificationCodeInput
            value={form.values.smsCode}
            onChange={(value) => form.setFieldValue('smsCode', value)}
            error={form.errors.smsCode}
            phone={form.values.phone}
            onValidatePhone={() => form.validateField('phone')}
            onSwitchToFaceid={handleSwitchToFaceid}
          />
        )}

        {showEmployeeCode && !employeeId && (
          <TextInput
            mt="md"
            placeholder="请输入员工编号"
            maxLength={5}
            leftSection={<IconBriefcase size={16} />}
            {...form.getInputProps('employeeCode')}
          />
        )}

        <TermsCheckbox
          checked={form.values.termsOfService}
          onChange={(checked) => form.setFieldValue('termsOfService', checked)}
          termsContent={getTermsContent()}
        />

        <SubmitButton price={template?.price} loading={creatingOrder} themeConfig={themeConfig} />
      </form>
    </Box>
  )
}
